import { useCallback, useRef, useState, useEffect } from "react";
import isEqual from "lodash.isequal";
import { PlaceholderExtension } from "@remirror/extension-placeholder";
import { LinkExtension } from "@remirror/extension-link";
import { wysiwygPreset } from "@remirror/preset-wysiwyg";
import {
  EditorComponent,
  Remirror,
  ThemeProvider,
  useRemirror,
  useEditorEvent,
  useHelpers,
} from "@remirror/react";
import { FloatingToolbar } from "@remirror/react-ui";
import { AllStyledComponent } from "@remirror/styles/emotion";

import type { UseThemeProps } from "@remirror/react";
import {
  RemirrorContentType,
  FrameworkProps,
  AnyExtension,
  CreateEditorStateProps,
  EditorView,
  RemirrorJSON,
  InvalidContentHandler,
  PlainExtension,
} from "@remirror/core";

import { CustomImageExtension, LinkTooltipExtension } from "../../extensions";
import { useLinkSecurityHandler, LinkSecurityAlert } from "../LinkSecurity";
import { ALLOWED_TLDS } from "../../constants";

import "./WysiwygEditor.css";

export type ContentType = RemirrorContentType;

class PasteHandlerExtension extends PlainExtension {
  get name() {
    return "pasteHandler" as const;
  }

  createPlugin() {
    return {
      props: {
        handlePaste: (view: EditorView, event: ClipboardEvent) => {
          if (event.clipboardData) {
            const text = event.clipboardData.getData("text");
            const { state, dispatch } = view;

            const isURL = /^(https?:\/\/[^\s]+)/.test(text);
            const { from, to } = state.selection;
            const hasTextSelection = from !== to;

            if (dispatch && isURL && hasTextSelection) {
              const linkMark = state.schema.marks.link.create({ href: text });
              const linkText = state.doc.textBetween(from, to);
              const textNode = state.schema.text(linkText, [linkMark]);

              // Replace the selected text with the link mark
              dispatch(state.tr.replaceWith(from, to, textNode));
              return true;
            }

            return false;
          }

          return false; // Let ProseMirror handle the event if clipboardData is not available
        },
      },
    };
  }
}

export interface ReactEditorProps<T extends AnyExtension>
  extends Pick<CreateEditorStateProps, "stringHandler">,
    Pick<FrameworkProps<T>, "editable" | "autoFocus" | "classNames"> {
  placeholder?: string;
  theme?: UseThemeProps["theme"];
  initialContent: RemirrorContentType | undefined;
  onBlur: (data: RemirrorContentType) => void;
  nodeId: string;
  canvasId: string;
  onImageUploadStart: () => void;
  onImageUploadError: () => void;
  isPublic: boolean;
  blockedDomains?: string[];
}

export function WysiwygEditor<T extends AnyExtension>({
  placeholder,
  stringHandler,
  theme,
  initialContent,
  canvasId,
  onImageUploadStart,
  onImageUploadError,
  onBlur,
  editable,
  isPublic,
  blockedDomains = [],
  ...rest
}: ReactEditorProps<T>) {
  // Link security handler with state management
  const {
    alertType,
    handleLinkClick,
    handleConfirmationCancel,
    handleConfirmationContinue,
    handleBlockedDismiss,
  } = useLinkSecurityHandler({ isPublic, blockedDomains });

  // Alert position state
  const [alertPosition, setAlertPosition] = useState({ top: 0, left: 0 });

  const extensions = useCallback(() => {
    const linkExtension = new LinkExtension({
      autoLink: true,
      defaultTarget: "_blank",
      defaultProtocol: "https:",
      autoLinkAllowedTLDs: ALLOWED_TLDS,
      openLinkOnClick: !isPublic, // Disable link opening on public canvases for security
    });

    return [
      new PlaceholderExtension({ placeholder }),
      linkExtension,
      new PasteHandlerExtension(),
      new LinkTooltipExtension(),
      new CustomImageExtension({
        canvasId,
        onImageUploadStart,
        onImageUploadError,
        enableResizing: true,
      }),
      ...wysiwygPreset(), // This must go last to ensure our custom extensions are not overwritten by the preset
    ];
  }, [isPublic, placeholder, canvasId, onImageUploadStart, onImageUploadError]);

  // DOM event listener for link clicks (needed for positioning)
  const editorContainerRef = useRef<HTMLDivElement>(null);

  // Note: We use DOM event listener instead of Remirror handler
  // because we need access to the actual anchor element for positioning
  useEffect(() => {
    // Only intercept link clicks on public canvases for security
    if (!isPublic) return;

    const container = editorContainerRef.current;
    if (!container) return;

    const handleClick = (event: Event) => {
      const target = event.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement;

      if (anchor && anchor.getAttribute("href")) {
        event.preventDefault();
        event.stopPropagation();

        // Set alert position near the clicked anchor
        // For sidebar context (Notes), position relative to the sidebar container
        const editorContainer =
          container.closest(".remirror-theme") || container;
        const containerRect = editorContainer.getBoundingClientRect();
        const anchorRect = anchor.getBoundingClientRect();

        // Always use container-relative positioning for sidebar
        // Position below the anchor, aligned with the left edge of the notes section
        const alertPosition = {
          top: anchorRect.bottom - containerRect.top + 9,
          left: 0,
        };

        setAlertPosition(alertPosition);
        handleLinkClick(anchor.href);
      }
    };

    // Use capture phase to intercept before other handlers
    container.addEventListener("click", handleClick, true);
    return () => {
      container.removeEventListener("click", handleClick, true);
    };
  }, [handleLinkClick, isPublic]);

  const onError: InvalidContentHandler = useCallback(
    ({ json, invalidContent, transformers }) => {
      // Automatically remove all invalid nodes and marks.
      return transformers.remove(json, invalidContent);
    },
    [],
  );

  const { manager, state } = useRemirror({
    extensions,
    stringHandler,
    content: initialContent,
    onError,
  });

  return (
    <div ref={editorContainerRef} className="wysiwyg-editor-wrapper">
      <AllStyledComponent>
        <ThemeProvider theme={theme}>
          <Remirror
            manager={manager}
            initialContent={state}
            editable={editable}
            {...rest}
          >
            {editable && <FloatingToolbar />}
            <Editor initialContent={initialContent} onBlur={onBlur} />
          </Remirror>
        </ThemeProvider>
        <LinkSecurityAlert
          alertType={alertType}
          alertPosition={alertPosition}
          useAbsolutePositioning={true}
          onConfirmationCancel={handleConfirmationCancel}
          onConfirmationContinue={handleConfirmationContinue}
          onBlockedDismiss={handleBlockedDismiss}
        />
      </AllStyledComponent>
    </div>
  );
}

function Editor({
  onBlur,
  initialContent,
}: {
  onBlur: (content: RemirrorJSON) => void;
  initialContent: RemirrorContentType | undefined;
}) {
  const { getJSON } = useHelpers();
  const previousJSON = useRef<RemirrorContentType | undefined>(initialContent);

  const handleOnBlur = useCallback(() => {
    const currentJSON = getJSON();
    const hasChanges = !isEqual(previousJSON.current, currentJSON);
    if (hasChanges) {
      onBlur(currentJSON);
      previousJSON.current = currentJSON;
    }
  }, [getJSON, onBlur]);

  useEditorEvent("blur", handleOnBlur);

  return <EditorComponent />;
}
